import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Spinner, Alert, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import {
  Plus,
  Package,
  TrendingDown,
  CalendarClock,
  Upload,
  Scale,
  ArrowRight,
  ChevronRight,
  Search as SearchIcon,
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { ListToolbar, FilterChips } from '../../Common/ListToolbar';
import { EmptyState } from '../../Common/EmptyState';
import { useListControls, useViewMode } from '../../../../hooks/useListControls';
import { useCapabilities } from '../../../../context/CapabilityContext';
import PageHeader from '../../../layouts/PageHeader';
import CreateAssetForm from './CreateAssetForm';
import AssetImportModal from './AssetImportModal';
import AssetNudgeCard from './AssetNudgeCard';
import RunHistory from './RunHistory';
import K1YearEndPanel from './K1YearEndPanel';
import {
  ASSET_CATEGORIES,
  CategoryChip,
  StatusPill,
  addMonths,
  formatCurrency,
  formatMonthYear,
  formatNumber,
  getAssetAccountingUnavailable,
  getCategoryMeta,
  lastDayOfMonth,
  periodToDate,
  round2,
  unwrapData,
  isK1Collective,
} from './assetUtils';

/**
 * Tillgångar — the fixed-asset register. Mirrors the Inkorg page anatomy
 * (search + sort + list/grid toggle + badge-count filter chips) with a
 * Digits-style dense table as the default list view. All data loading and
 * suggestion handling is unchanged from the first implementation — this
 * file's redo is visual/UX only.
 */

// Filter chips — replaces the old three-tab Nav. Status chips filter the
// register; 'suggested' and 'runs' swap in their own panels below the chips.
const FILTER_CHIPS = [
  { key: 'all', label: 'Alla' },
  { key: 'active', label: 'Aktiva' },
  { key: 'suggested', label: 'Föreslagna' },
  { key: 'fully_depreciated', label: 'Helt avskrivna' },
  { key: 'disposed', label: 'Avvecklade' },
  { key: 'runs', label: 'Körningar', countless: true },
];

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Senaste först' },
  { value: 'name_asc', label: 'Namn (A–Ö)' },
  { value: 'value_desc', label: 'Värde (högst först)' },
];

const CATEGORY_OPTIONS = ASSET_CATEGORIES.map((c) => ({ value: c.code, label: c.label }));

// Bokfört värde with defensive fallbacks — the register derives it from
// events server-side; older payload shapes may name it differently.
const getBookValue = (asset) => {
  // Disposed assets are fully derecognized — the value left the books via
  // the disposal verifikat.
  if (asset.status === 'disposed') return 0;
  const direct = asset.book_value ?? asset.remaining_value;
  if (direct !== undefined && direct !== null) return round2(direct);
  const acquisition = parseFloat(asset.acquisition_value) || 0;
  const acc = parseFloat(asset.accumulated_depreciation) || 0;
  return round2(acquisition - acc);
};

const getEndDate = (asset) => {
  const period = asset.schedule_end_period || asset.end_period;
  if (period) return periodToDate(period) || new Date(period);
  const start = asset.in_service_date ? new Date(asset.in_service_date) : null;
  const months = parseInt(asset.useful_months, 10);
  if (!start || isNaN(start.getTime()) || !months) return null;
  return addMonths(new Date(start.getFullYear(), start.getMonth(), 1), months - 1);
};

// 0–100: how much of the depreciable amount is consumed.
const getProgress = (asset, bookValue) => {
  const acquisition = parseFloat(asset.acquisition_value) || 0;
  const residual = parseFloat(asset.residual_value) || 0;
  const depreciable = Math.max(acquisition - residual, 0);
  if (depreciable <= 0) return 100;
  return Math.min(Math.max(((depreciable - Math.max(bookValue - residual, 0)) / depreciable) * 100, 0), 100);
};

const RemainingBar = ({ progress, disposed }) => (
  <div className="asset-bar" aria-hidden="true">
    <div
      className="asset-bar-fill"
      style={{ width: `${disposed ? 100 : progress}%`, opacity: disposed ? 0.25 : 1 }}
    />
  </div>
);

// One register row — the Digits-style dense list line:
// [icon chip] [name + muted secondary] [status] [bokfört värde] [bar] [chevron]
const AssetRow = ({ asset, onClick }) => {
  const bookValue = getBookValue(asset);
  const acquisition = parseFloat(asset.acquisition_value) || 0;
  const progress = getProgress(asset, bookValue);
  const endDate = getEndDate(asset);
  const category = getCategoryMeta(asset.category_code || asset.category);
  const isDisposed = asset.status === 'disposed';
  // K1 pool assets are valued collectively (BFNAR 2006:1 p. 6.38) — a
  // per-asset "bokfört värde" would be false. Show the register facts only.
  const isCollective = asset.accounting_model === 'k1_collective_pool';

  return (
    <button type="button" className="asset-row" onClick={onClick}>
      <CategoryChip code={category.code} tone={isDisposed ? 'neutral' : 'brand'} />
      <span className="asset-row-main">
        <span className="asset-row-name" title={asset.name}>
          {asset.name}
          {asset.asset_number && <span className="asset-row-number">#{asset.asset_number}</span>}
          {parseInt(asset.quantity, 10) > 1 && (
            <span className="asset-row-number">× {asset.quantity}</span>
          )}
        </span>
        <span className="asset-row-sub">
          {category.label}
          {asset.in_service_date ? ` · ${formatMonthYear(asset.in_service_date)}` : ''}
          {` · ansk. ${formatCurrency(acquisition)}`}
        </span>
      </span>
      <span className="asset-row-status"><StatusPill status={asset.status} /></span>
      <span className="asset-row-value asset-num">
        {isCollective ? '—' : `${formatNumber(bookValue, 0)} kr`}
      </span>
      <span className="asset-row-bar">
        {isCollective ? (
          <span className="asset-row-bar-label">Kollektiv värdering (K1)</span>
        ) : (
          <>
            <RemainingBar progress={progress} disposed={isDisposed} />
            <span className="asset-row-bar-label">
              {isDisposed
                ? `Avvecklad${asset.disposed_at ? ` ${formatMonthYear(asset.disposed_at)}` : ''}`
                : endDate ? `till ${formatMonthYear(endDate)}` : `${Math.round(progress)} % avskriven`}
            </span>
          </>
        )}
      </span>
      <ChevronRight size={16} className="asset-row-chevron" />
    </button>
  );
};

// Grid tile — same information as a card, app card conventions.
const AssetCard = ({ asset, onClick }) => {
  const bookValue = getBookValue(asset);
  const progress = getProgress(asset, bookValue);
  const endDate = getEndDate(asset);
  const category = getCategoryMeta(asset.category_code || asset.category);
  const isDisposed = asset.status === 'disposed';
  const isCollective = asset.accounting_model === 'k1_collective_pool';

  return (
    <div
      className="asset-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div className="asset-card-top">
        <CategoryChip code={category.code} size={40} iconSize={19} tone={isDisposed ? 'neutral' : 'brand'} />
        <StatusPill status={asset.status} />
      </div>
      <div className="asset-card-name" title={asset.name}>
        {asset.name}
        {parseInt(asset.quantity, 10) > 1 && (
          <span className="asset-row-number"> × {asset.quantity}</span>
        )}
      </div>
      <div className="asset-card-sub">
        {category.label}
        {asset.in_service_date ? ` · ${formatMonthYear(asset.in_service_date)}` : ''}
      </div>
      <div className="asset-card-value asset-num">
        {isCollective ? '—' : `${formatNumber(bookValue, 0)} kr`}
      </div>
      {!isCollective && <RemainingBar progress={progress} disposed={isDisposed} />}
      <div className="asset-card-meta">
        {isCollective
          ? 'Kollektiv värdering (K1)'
          : isDisposed
          ? `Avvecklad${asset.disposed_at ? ` ${formatMonthYear(asset.disposed_at)}` : ''}`
          : endDate ? `Avskrivs till ${formatMonthYear(endDate)}` : `${Math.round(progress)} % avskriven`}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="stat-card">
    <div className="stat-icon" style={{ background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}>
      <Icon size={22} />
    </div>
    <div className="stat-value" style={{ color: 'var(--title)' }}>{value}</div>
    <div className="stat-label">{label}</div>
    {sub && <div className="stat-subtitle">{sub}</div>}
  </div>
);

const ASSET_MODEL_CONFIRMATION = 'I_CONFIRM_DOCUMENTED_K2_ASSET_MODEL_COMPATIBILITY';
const ASSET_MODEL_WITHDRAWAL = 'I_WITHDRAW_K2_ASSET_MODEL_COMPATIBILITY';

const AssetModelConfirmationModal = ({ show, onHide, companyId, onConfirmed }) => {
  const [assessmentReference, setAssessmentReference] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/accounting-model/confirm-k2`),
        {
          model: 'k2_individual_v1',
          confirmation: ASSET_MODEL_CONFIRMATION,
          compatibilityConfirmed: confirmed,
          existingAssetsReviewed: confirmed,
          assessmentReference: assessmentReference.trim(),
        },
        { withCredentials: true }
      );
      toast.success('Den K2-kompatibla tillgångsmodellen är bekräftad.');
      setAssessmentReference('');
      setConfirmed(false);
      setSaving(false);
      onHide();
      await onConfirmed();
    } catch (err) {
      setError(err.response?.data?.error || 'Bekräftelsen kunde inte sparas.');
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Bekräfta K2-kompatibel tillgångsmodell</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          Detta väljer inte företagets K-regelverk. Bekräftelsen gäller tills
          den återkallas och visar att en kvalificerad rådgivare har bedömt att
          LedgerLoops individuella K2-modell kan användas för företagets
          tillgångar. Återkalla den om förutsättningarna ändras. Bekräfta inte
          för K1- eller K3-redovisning.
        </Alert>
        <Form.Group controlId="asset-model-assessment-reference">
          <Form.Label>Hänvisning till rådgivarens dokumenterade bedömning</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={500}
            value={assessmentReference}
            onChange={(event) => setAssessmentReference(event.target.value)}
            placeholder="Exempel: Rådgivningsanteckning 2026-04-12, ärende 42"
          />
          <Form.Text className="text-muted">Minst 10 tecken. Hänvisningen sparas i revisionsspåret.</Form.Text>
        </Form.Group>
        <Form.Check
          className="mt-3"
          id="asset-model-confirmation"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          label="Jag bekräftar att rådgivaren har bedömt K2-kompatibiliteten och granskat alla befintliga tillgångar."
        />
        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Avbryt</Button>
        <Button
          variant="primary"
          onClick={submit}
          disabled={saving || !confirmed || assessmentReference.trim().length < 10}
        >
          {saving ? 'Sparar…' : 'Bekräfta K2-kompatibilitet'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AssetModelWithdrawalModal = ({ show, onHide, companyId, onWithdrawn }) => {
  const [withdrawalReference, setWithdrawalReference] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/accounting-model/withdraw-k2`),
        {
          confirmation: ASSET_MODEL_WITHDRAWAL,
          withdrawalReference: withdrawalReference.trim(),
        },
        { withCredentials: true }
      );
      toast.success('K2-kompatibiliteten har återkallats. Tillgångsmodulen är nu skrivskyddad.');
      setWithdrawalReference('');
      setConfirmed(false);
      setSaving(false);
      onHide();
      await onWithdrawn();
    } catch (err) {
      setError(err.response?.data?.error || 'Återkallelsen kunde inte sparas.');
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Återkalla K2-kompatibilitet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          Efter återkallelsen stoppas alla K2-beräkningar och ändringar i
          tillgångsmodulen. Sparade registeruppgifter finns kvar och kan läsas.
          Återkallelsen kan inte ångras i den här funktionen.
        </Alert>
        <Form.Group controlId="asset-model-withdrawal-reference">
          <Form.Label>Dokumenterad anledning eller hänvisning</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={500}
            value={withdrawalReference}
            onChange={(event) => setWithdrawalReference(event.target.value)}
            placeholder="Exempel: Byte till K3 enligt rådgivarärende 73"
          />
          <Form.Text className="text-muted">
            Minst 10 tecken. Den ursprungliga bekräftelsen bevaras.
          </Form.Text>
        </Form.Group>
        <Form.Check
          className="mt-3"
          id="asset-model-withdrawal-confirmation"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          label="Jag förstår att K2-beräkningar och bokföring stoppas efter återkallelsen."
        />
        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Avbryt</Button>
        <Button
          variant="danger"
          onClick={submit}
          disabled={saving || !confirmed || withdrawalReference.trim().length < 10}
        >
          {saving ? 'Sparar…' : 'Återkalla K2-kompatibilitet'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AssetList = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { capabilities } = useCapabilities();

  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountingUnavailable, setAccountingUnavailable] = useState(null);
  const [showModelConfirmation, setShowModelConfirmation] = useState(false);
  const [showModelWithdrawal, setShowModelWithdrawal] = useState(false);
  // Filter/search/sort live in the URL so a narrowed register survives a
  // refresh and can be linked; view mode stays a local preference.
  const {
    search: searchInput,
    setSearch: setSearchInput,
    debouncedSearch,
    filter: activeFilter,
    setFilter: setActiveFilter,
    sort: sortKey,
    setSort: setSortKey,
    isFiltered,
  } = useListControls({ defaultFilter: 'all', defaultSort: 'date_desc' });
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [viewMode, changeView] = useViewMode('assets', 'list', ['list', 'grid']);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [busySuggestionId, setBusySuggestionId] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [taxNudgeYear, setTaxNudgeYear] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccountingUnavailable(null);
    try {
      const [assetsRes, summaryRes, suggestionsRes] = await Promise.allSettled([
        axios.get(getApiUrl(`${companyId}/assets`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/assets/summary`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/assets/suggestions`), { withCredentials: true }),
      ]);

      if (assetsRes.status === 'fulfilled') {
        const data = unwrapData(assetsRes.value);
        setAssets(Array.isArray(data) ? data : data?.assets || []);
      } else {
        const unavailable = getAssetAccountingUnavailable(assetsRes.reason);
        if (unavailable) setAccountingUnavailable(unavailable);
        else setError('Det gick inte att hämta tillgångarna. Försök igen senare.');
      }
      if (summaryRes.status === 'fulfilled') {
        const data = unwrapData(summaryRes.value);
        setSummary(data);
        const unavailable = getAssetAccountingUnavailable(data?.accountingCapability);
        if (unavailable) setAccountingUnavailable(unavailable);
      } else {
        const unavailable = getAssetAccountingUnavailable(summaryRes.reason);
        if (unavailable) setAccountingUnavailable(unavailable);
      }
      if (suggestionsRes.status === 'fulfilled') {
        const data = unwrapData(suggestionsRes.value);
        const list = Array.isArray(data) ? data : data?.suggestions || [];
        setSuggestions(list.filter((s) => !s.status || s.status === 'open'));
      }
    } catch (err) {
      setError('Det gick inte att hämta tillgångarna. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Deep link from the K1 year-end panel (Gate 1 F3): /assets?skapa=1 opens
  // the create form so "registrera befintliga tillgångar" is one click.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('skapa') === '1') setShowCreate(true);
  }, [location.search]);

  // Bokslutsknuff: visa skattestegs-bannern när ett räkenskapsår närmar sig
  // sitt slut (≤ 60 dagar kvar) eller nyligen har passerat (bokslutssäsong,
  // ~7 månader — deklarationsfristen). Best-effort: fel sväljs tyst.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await axios.get(
          getApiUrl(`${companyId}/accounting/fiscalyears`),
          { withCredentials: true }
        );
        if (cancelled) return;
        const today = new Date();
        const candidate = (result.data?.fiscalYears || [])
          .map((fy) => ({ ...fy, endDate: new Date(fy.end_date) }))
          .filter((fy) => !isNaN(fy.endDate.getTime()))
          .sort((a, b) => b.endDate - a.endDate)
          .find((fy) => {
            const days = (fy.endDate - today) / 86400000;
            return days <= 60 && days >= -210;
          });
        if (candidate) setTaxNudgeYear(candidate);
      } catch (err) {
        // Bannern är en knuff, inte kritisk — visa bara ingenting.
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Chip counts — computed from the loaded register + suggestions.
  const chipCounts = useMemo(() => ({
    all: assets.length,
    active: assets.filter((a) => a.status === 'active').length,
    suggested: suggestions.length,
    fully_depreciated: assets.filter((a) => a.status === 'fully_depreciated').length,
    disposed: assets.filter((a) => a.status === 'disposed').length,
  }), [assets, suggestions]);

  const query = debouncedSearch.toLowerCase();

  // Status chip → search → category filter → sort. All client-side; the
  // register is already fully loaded.
  const visibleAssets = useMemo(() => {
    let list = assets;
    if (activeFilter !== 'all' && activeFilter !== 'suggested' && activeFilter !== 'runs'
        && activeFilter !== 'k1_yearend') {
      list = list.filter((a) => a.status === activeFilter);
    }
    if (categoryFilter) {
      list = list.filter((a) => (a.category_code || a.category) === categoryFilter.value);
    }
    if (query) {
      list = list.filter((a) => {
        const category = getCategoryMeta(a.category_code || a.category);
        return [a.name, a.serial_number, a.asset_number, category.label]
          .some((field) => field !== undefined && field !== null
            && String(field).toLowerCase().includes(query));
      });
    }
    const sorted = [...list];
    if (sortKey === 'name_asc') {
      sorted.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));
    } else if (sortKey === 'value_desc') {
      sorted.sort((a, b) => getBookValue(b) - getBookValue(a));
    } else {
      sorted.sort((a, b) => String(b.in_service_date || b.acquisition_date || '')
        .localeCompare(String(a.in_service_date || a.acquisition_date || '')));
    }
    return sorted;
  }, [assets, activeFilter, categoryFilter, query, sortKey]);

  // "Sätt upp avskrivning" på ett förslag: accept → prefilled CreateAssetForm.
  const handleAcceptSuggestion = async (suggestion) => {
    setBusySuggestionId(suggestion.suggestion_id);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/assets/suggestions/${suggestion.suggestion_id}/accept`),
        {},
        { withCredentials: true }
      );
      const data = unwrapData(response) || {};
      setCreatePrefill({
        ...(suggestion.prefill || {}),
        ...(data.prefill || {}),
        suggestion_id: suggestion.suggestion_id,
        ...(suggestion.source_type === 'supplier_invoice' && { supplier_invoice_id: suggestion.source_id }),
        ...(suggestion.source_type === 'expense' && { expense_id: suggestion.source_id }),
      });
      setShowCreate(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte hämta förslaget.');
    } finally {
      setBusySuggestionId(null);
    }
  };

  // Retroactive sweep: scan booked verifikat for asset-shaped purchases
  // without register posts (12xx debits, threshold-crossing cost rows).
  const handleSweep = async () => {
    setSweeping(true);
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/assets/detection/sweep`),
        {},
        { withCredentials: true }
      );
      const result = unwrapData(response) || {};
      toast.success(
        result.filed > 0
          ? `${result.filed} nya förslag hittades.`
          : 'Inga nya förslag — bokföringen ser komplett ut.'
      );
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte söka igenom bokföringen.');
    } finally {
      setSweeping(false);
    }
  };

  const handleDismissSuggestion = async (suggestion) => {
    setBusySuggestionId(suggestion.suggestion_id);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/suggestions/${suggestion.suggestion_id}/dismiss`),
        {},
        { withCredentials: true }
      );
      setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== suggestion.suggestion_id));
      toast.success('Okej — inköpet bokförs som vanlig kostnad.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte avfärda förslaget.');
    } finally {
      setBusySuggestionId(null);
    }
  };

  const openCreateBlank = () => {
    setCreatePrefill(null);
    setShowCreate(true);
  };

  // Summary keys are camelCase from the backend service; older payloads used
  // snake_case — accept both.
  const totalBookValue = summary?.totalBookValue ?? summary?.total_book_value ?? summary?.book_value ?? 0;
  const monthlyDepreciation = summary?.monthlyDepreciation ?? summary?.monthly_depreciation ?? summary?.monthly_amount ?? 0;
  // The run is scheduled for the last day of every month; when something is
  // still depreciating that IS the next run.
  const nextRun = summary?.next_run_date || summary?.next_run
    || (parseFloat(monthlyDepreciation) > 0 ? lastDayOfMonth(new Date()) : null);
  const isK1 = isK1Collective(summary?.accountingCapability);
  const showRegister = activeFilter !== 'suggested' && activeFilter !== 'runs'
    && activeFilter !== 'k1_yearend';
  const activeModelApproval = summary?.accountingCapability?.assetAccountingModelApproval;
  // K1: monthly runs do not exist — the year-end valuation panel replaces
  // the runs tab, and "helt avskriven" has no per-asset meaning.
  const filterChips = isK1
    ? FILTER_CHIPS.filter((c) => !['runs', 'fully_depreciated'].includes(c.key))
      .concat([{ key: 'k1_yearend', label: 'Årets värdering (K1)', countless: true }])
    : FILTER_CHIPS;

  // Two genuinely different situations: an empty register (teach what an asset
  // is, offer the first action) versus a filter that hides everything.
  const renderEmptyRegister = () =>
    assets.length === 0 ? (
      <EmptyState
        icon={Package}
        title="Inga tillgångar ännu"
        description="En tillgång är något företaget köper och använder i flera år — som en dator eller bil. Istället för att dra av hela kostnaden direkt fördelas den över åren, det kallas avskrivning."
        actions={
          <>
            <Button variant="primary" className="d-inline-flex align-items-center gap-1" onClick={openCreateBlank}>
              <Plus size={16} /> Lägg till tillgång
            </Button>
            <Button
              variant="outline-primary"
              className="d-inline-flex align-items-center gap-1"
              onClick={() => setShowImport(true)}
            >
              <Upload size={16} /> Importera från annat system
            </Button>
          </>
        }
      />
    ) : (
      <EmptyState
        variant="no-matches"
        query={debouncedSearch}
        title={isFiltered ? 'Inga träffar' : 'Inga tillgångar i denna vy'}
        description="Byt filter för att se andra tillgångar."
      />
    );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Laddar tillgångar…</p>
        </div>
      </div>
    );
  }

  if (accountingUnavailable) {
    return (
      <>
        <div className="asset-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <PageHeader
            icon={Package}
            title="Tillgångar"
            subtitle="Anläggningsregister och avskrivningar"
          />
          <Alert variant="warning" className="mb-0">
            <Alert.Heading>{accountingUnavailable.heading}</Alert.Heading>
            <p className="mb-1">{accountingUnavailable.message}</p>
            <p className="mb-0">
              Du kan läsa redan registrerade uppgifter, men inte skapa, ändra,
              skriva av eller avyttra tillgångar i modulen.
            </p>
            {accountingUnavailable.classificationAllowed && capabilities?.isOwner && (
              <Button
                variant="outline-dark"
                size="sm"
                className="mt-3"
                onClick={() => setShowModelConfirmation(true)}
              >
                Bekräfta dokumenterad K2-kompatibilitet
              </Button>
            )}
          </Alert>
          {assets.length > 0 ? (
            <div className="asset-list-box">
              {assets.map((asset) => (
                <AssetRow
                  key={asset.asset_id}
                  asset={asset}
                  onClick={() => history.push(`/${companyId}/assets/${asset.asset_id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="modern-card mb-0">
              <div style={{ padding: '1.25rem 1.5rem', color: 'var(--secondary)' }}>
                Inga tillgångar är registrerade. Fullständigt stöd för{' '}
                {accountingUnavailable.accountingRegime
                  ? accountingUnavailable.accountingRegime.toUpperCase()
                  : 'det här redovisningsregelverket'}{' '}
                behöver införas i en separat, granskad implementation.
              </div>
            </div>
          )}
        </div>
        <AssetModelConfirmationModal
          show={showModelConfirmation}
          onHide={() => setShowModelConfirmation(false)}
          companyId={companyId}
          onConfirmed={fetchData}
        />
      </>
    );
  }

  return (
    <>
      <div className="asset-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <PageHeader
          icon={Package}
          title="Tillgångar"
          subtitle="Saker företaget använder i flera år — LedgerLoop sköter avskrivningen automatiskt."
        />

        {activeModelApproval?.lifecycle_status === 'active' && capabilities?.isOwner && (
          <Alert variant="info" className="mb-0 d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <span>
              Den dokumenterade K2-kompatibiliteten gäller tills den återkallas.
              Återkalla den om företagets regelverk eller förutsättningar ändras.
            </span>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setShowModelWithdrawal(true)}
            >
              Återkalla K2-kompatibilitet
            </Button>
          </Alert>
        )}

        {error && <Alert variant="danger" className="mb-0">{error}</Alert>}

        {/* Bokslutsknuff → skattesteget (progressive disclosure: skatte-
            spåret är en bokslutshändelse och marknadsförs bara här). */}
        {taxNudgeYear && assets.length > 0 && (
          <div
            className="modern-card"
            style={{ border: '1px solid var(--rgba-primary-2)', background: 'var(--rgba-primary-1)', marginBottom: 0 }}
          >
            <div style={{ padding: '1rem 1.4rem' }} className="d-flex align-items-center gap-3 flex-wrap">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: '#fff',
                  color: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Scale size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 700, color: 'var(--title)' }}>
                  Dags för bokslut? Se din skattemässiga avskrivning
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
                  Räkenskapsåret {taxNudgeYear.year}{' '}
                  {taxNudgeYear.endDate > new Date() ? 'slutar' : 'slutade'}{' '}
                  {formatMonthYear(taxNudgeYear.end_date, 'long')} — skattemässigt får
                  du ofta skriva av mer än planen visar.
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => (isK1
                  ? setActiveFilter('k1_yearend')
                  : history.push(`/${companyId}/assets/tax`))}
              >
                {isK1 ? 'Till årsvärderingen' : 'Till bokslutssteget'} <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* Summary strip — the app's stat-card pattern (InvoiceList/QuotesList). */}
        <div className="stats-grid">
          <StatCard
            icon={Package}
            label="Totalt bokfört värde"
            value={formatCurrency(totalBookValue)}
          />
          {isK1 ? (
            <>
              <StatCard
                icon={TrendingDown}
                label="Avskrivning"
                value="Årsvis (K1)"
                sub="Kollektiv värdering vid bokslut (BFNAR 2006:1)"
              />
              <StatCard
                icon={CalendarClock}
                label="Årets värdering"
                value="Vid bokslut"
                sub="Öppna fliken Årets värdering (K1)"
              />
            </>
          ) : (
            <>
              <StatCard
                icon={TrendingDown}
                label="Månadens avskrivning"
                value={formatCurrency(monthlyDepreciation)}
              />
              <StatCard
                icon={CalendarClock}
                label="Nästa körning"
                value={nextRun ? formatMonthYear(nextRun, 'long') : '—'}
                sub={nextRun ? 'Bokförs automatiskt' : 'Inga planerade avskrivningar'}
              />
            </>
          )}
        </div>

        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: 'Sök namn, serienummer, kategori…',
            ariaLabel: 'Sök bland tillgångar',
          }}
          sort={{ options: SORT_OPTIONS, value: sortKey, onChange: setSortKey, ariaLabel: 'Sortera tillgångar' }}
          view={{ value: viewMode, onChange: changeView, modes: ['list', 'grid'] }}
          actions={
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="d-flex align-items-center gap-1"
                onClick={() => setShowImport(true)}
              >
                <Upload size={14} /> Importera
              </Button>
              <Button variant="primary" size="sm" className="d-flex align-items-center gap-1" onClick={openCreateBlank}>
                <Plus size={14} /> Lägg till tillgång
              </Button>
            </>
          }
        >
          <div style={{ minWidth: 200 }}>
            <Select
              options={CATEGORY_OPTIONS}
              value={categoryFilter}
              onChange={setCategoryFilter}
              placeholder="Kategori"
              isClearable
              theme={brandSelectTheme}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
                option: (base) => ({ ...base, fontSize: 13 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              aria-label="Filtrera på kategori"
            />
          </div>
        </ListToolbar>

        <FilterChips
          chips={filterChips.map((c) => ({ ...c, count: chipCounts[c.key] }))}
          value={activeFilter}
          onChange={setActiveFilter}
        />

        {/* Register — dense list (default) or card grid. */}
        {showRegister && (
          visibleAssets.length === 0 ? (
            renderEmptyRegister()
          ) : viewMode === 'grid' ? (
            <div className="asset-grid">
              {visibleAssets.map((asset) => (
                <AssetCard
                  key={asset.asset_id}
                  asset={asset}
                  onClick={() => history.push(`/${companyId}/assets/${asset.asset_id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="asset-list-box">
              {/* Muted tiny uppercase column labels (Digits table header). */}
              <div className="asset-row asset-row-head" aria-hidden="true">
                <span />
                <span className="asset-col-label">Tillgång</span>
                <span className="asset-col-label">Status</span>
                <span className="asset-col-label" style={{ textAlign: 'right' }}>Bokfört värde</span>
                <span className="asset-col-label">Avskrivning</span>
                <span />
              </div>
              {visibleAssets.map((asset) => (
                <AssetRow
                  key={asset.asset_id}
                  asset={asset}
                  onClick={() => history.push(`/${companyId}/assets/${asset.asset_id}`)}
                />
              ))}
            </div>
          )
        )}

        {activeFilter === 'suggested' && (
          suggestions.length === 0 ? (
            <div className="asset-state-box" style={{ padding: '2.5rem 2rem', textAlign: 'center', color: '#6b7280' }}>
              <p className="mb-1" style={{ fontWeight: 600 }}>Inga förslag just nu.</p>
              <p className="mb-3" style={{ fontSize: '0.875rem' }}>
                När ett inköp ser ut som en tillgång dyker det upp här så att du kan
                sätta upp avskrivningen med ett klick.
              </p>
              <Button
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center gap-2"
                disabled={sweeping}
                onClick={handleSweep}
              >
                <SearchIcon size={15} /> {sweeping ? 'Söker…' : 'Sök igenom bokföringen'}
              </Button>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-end mb-2">
                <Button
                  variant="link"
                  size="sm"
                  style={{ textDecoration: 'none', color: 'var(--primary)', padding: 0 }}
                  disabled={sweeping}
                  onClick={handleSweep}
                >
                  {sweeping ? 'Söker…' : 'Sök igenom bokföringen igen'}
                </Button>
              </div>
              {suggestions.map((suggestion) => (
                <AssetNudgeCard
                  key={suggestion.suggestion_id}
                  suggestion={suggestion}
                  busy={busySuggestionId === suggestion.suggestion_id}
                  onSetup={handleAcceptSuggestion}
                  onExpense={handleDismissSuggestion}
                />
              ))}
            </div>
          )
        )}

        {activeFilter === 'k1_yearend' && (
          <K1YearEndPanel companyId={companyId} />
        )}

        {activeFilter === 'runs' && (
          <div className="modern-card" style={{ marginBottom: 0 }}>
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <RunHistory companyId={companyId} />
            </div>
          </div>
        )}
      </div>

      <AssetModelWithdrawalModal
        show={showModelWithdrawal}
        onHide={() => setShowModelWithdrawal(false)}
        companyId={companyId}
        onWithdrawn={fetchData}
      />

      <CreateAssetForm
        show={showCreate}
        onHide={() => setShowCreate(false)}
        companyId={companyId}
        prefill={createPrefill}
        onCreated={() => fetchData()}
      />

      <AssetImportModal
        show={showImport}
        onHide={() => setShowImport(false)}
        companyId={companyId}
        onImported={() => fetchData()}
      />

      {/* Co-located styles — mirrors the Inkorg page convention. */}
      <style>{`
        /* Toolbar view toggle — same as .inbox-view-toggle. */
        .asset-view-toggle { display: flex; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; flex-shrink: 0; }

        /* Stat strip — .stats-grid/.stat-card shared pattern (InvoiceList). */
        .asset-page .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.25rem; }
        .asset-page .stat-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem 1.5rem; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: box-shadow 0.2s ease; }
        .asset-page .stat-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.08); }
        .asset-page .stat-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 0.75rem; }
        .asset-page .stat-value { font-size: 1.35rem; font-weight: 700; margin-bottom: 0.25rem; font-variant-numeric: tabular-nums; }
        .asset-page .stat-label { color: var(--secondary); font-size: 0.75rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .asset-page .stat-subtitle { color: var(--secondary); font-size: 0.6875rem; margin-top: 0.125rem; }

        /* List + empty/loading container — mirrors .inbox-list-box. */
        .asset-list-box, .asset-state-box { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
        .asset-list-box { overflow-x: auto; }

        /* Digits-style dense rows: one grid template shared by header + rows
           so columns stay aligned. */
        .asset-row {
          display: grid;
          grid-template-columns: 34px minmax(220px, 1.7fr) 130px 120px minmax(150px, 1fr) 20px;
          gap: 14px;
          align-items: center;
          width: 100%;
          min-width: 760px;
          text-align: left;
          background: transparent;
          border: none;
          border-bottom: 1px solid #f3f4f6;
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.12s ease;
        }
        .asset-row:last-child { border-bottom: none; }
        .asset-row:hover { background: #f9fafb; }
        .asset-row:hover .asset-row-chevron { color: var(--primary); transform: translateX(2px); }
        .asset-row:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
        .asset-row-head { cursor: default; background: #f9fafb; border-bottom: 1px solid #e5e7eb; padding-top: 8px; padding-bottom: 8px; }
        .asset-row-head:hover { background: #f9fafb; }
        .asset-col-label { font-size: 0.6875rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; }
        .asset-row-main { min-width: 0; display: flex; flex-direction: column; gap: 1px; }
        .asset-row-name { font-weight: 600; font-size: 0.875rem; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .asset-row-number { font-weight: 500; font-size: 0.75rem; color: #9ca3af; margin-left: 6px; }
        .asset-row-sub { font-size: 0.75rem; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .asset-row-status { justify-self: start; }
        .asset-row-value { font-weight: 600; font-size: 0.875rem; color: #111827; text-align: right; white-space: nowrap; }
        .asset-num { font-variant-numeric: tabular-nums; }
        .asset-row-bar { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
        .asset-row-bar-label { font-size: 0.6875rem; color: #9ca3af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .asset-row-chevron { color: #d1d5db; transition: all 0.15s ease; }

        /* Remaining-value mini progress bar. */
        .asset-bar { height: 5px; background: #f3f4f6; border-radius: 999px; overflow: hidden; }
        .asset-bar-fill { height: 100%; border-radius: 999px; background: var(--primary); transition: width 0.3s ease; }

        /* Grid view — restyled cards on app card conventions. */
        .asset-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
        .asset-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; cursor: pointer; display: flex; flex-direction: column; gap: 6px; transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease; outline: none; }
        .asset-card:hover { border-color: var(--primary); box-shadow: 0 8px 20px rgba(0,0,0,0.08); transform: translateY(-2px); }
        .asset-card:focus-visible { border-color: var(--primary); box-shadow: 0 0 0 3px var(--rgba-primary-1); }
        .asset-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
        .asset-card-name { font-weight: 600; font-size: 0.9375rem; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .asset-card-sub { font-size: 0.75rem; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .asset-card-value { font-size: 1.125rem; font-weight: 700; color: #111827; margin-top: 4px; }
        .asset-card-meta { font-size: 0.6875rem; color: #9ca3af; margin-top: 2px; }

        @media (prefers-reduced-motion: reduce) {
          .asset-card, .asset-row, .asset-bar-fill, .asset-row-chevron { transition: none; }
        }
      `}</style>
    </>
  );
};

export default AssetList;
